import { useAuth } from '../context/AuthContext';
import { ROLES, ROLE_PERMISSIONS } from '../constants/roles';
import type { Role } from '../constants/roles';

/**
 * Hook to handle role-based access
 * Provides functions to verify roles and permissions of authenticated user
 */
export const useRoleAccess = () => {
  const { user, isAuthenticated } = useAuth();
  const currentRole = user?.role;

  /**
   * Checks if user has one of the required roles
   */
  const hasRole = (requiredRoles: Role | Role[]): boolean => {
    if (!isAuthenticated || !currentRole) return false;
    
    if (Array.isArray(requiredRoles)) {
      return requiredRoles.includes(currentRole);
    }
    
    return currentRole === requiredRoles;
  };

  /**
   * Checks if user has all required permissions
   */
  const hasPermissions = (requiredPermissions: (keyof typeof ROLE_PERMISSIONS[Role])[]): boolean => {
    if (!isAuthenticated || !currentRole) return false;
    
    return requiredPermissions.every(
      permission => ROLE_PERMISSIONS[currentRole][permission]
    );
  };

  /**
   * Checks if user has at least one of the required permissions
   */
  const hasAnyPermission = (requiredPermissions: (keyof typeof ROLE_PERMISSIONS[Role])[]): boolean => {
    if (!isAuthenticated || !currentRole) return false;
    
    return requiredPermissions.some(
      permission => ROLE_PERMISSIONS[currentRole][permission]
    );
  };

  /**
   * Gets all permissions of current user
   */
  const getUserPermissions = () => {
    if (!isAuthenticated || !currentRole) return null;
    
    return ROLE_PERMISSIONS[currentRole];
  };

  /**
   * Checks if user can access a specific section
   */
  const canAccessSection = (section: 'fiscalizacion' | 'observatorio'): boolean => {
    if (!isAuthenticated || !currentRole) return false;
    
    switch (section) {
      case 'fiscalizacion':
        return ROLE_PERMISSIONS[currentRole].canViewFiscalizacion;
      case 'observatorio':
        return ROLE_PERMISSIONS[currentRole].canViewObservatorio;
      default:
        return false;
    }
  };

  /**
   * Checks if user can edit in a specific section
   */
  const canEditInSection = (section: 'fiscalizacion' | 'observatorio'): boolean => {
    if (!isAuthenticated || !currentRole) return false;
    
    switch (section) {
      case 'fiscalizacion':
        return ROLE_PERMISSIONS[currentRole].canEditFiscalizacion;
      case 'observatorio':
        return ROLE_PERMISSIONS[currentRole].canEditObservatorio;
      default:
        return false;
    }
  };

  return {
    // State
    currentRole,
    isAuthenticated,
    
    // Verification functions
    hasRole,
    hasPermissions,
    hasAnyPermission,
    getUserPermissions,
    canAccessSection,
    canEditInSection,
    
    // Useful constants
    ROLES,
    ROLE_PERMISSIONS
  };
}; 